import { Modal } from '@components/ui/Modal';
import { AttachmentDto } from '@cloudclevr/components';
import { RecommendationDto } from '@hooks/roadmaps/types';
import { useIsMobile } from '@hooks/utils/isMobile';
import { useIsTablet } from '@hooks/utils/isTablet';
import { useGetAttachmentDownloadToken } from '@hooks/roadmaps/useRecommendationMutations';
import React, { useState } from 'react';
import { DownloadIcon } from '@components/ui/Icons/DownloadIcon';

export type DownloadAttachmentsModalProps = {
  recommendation: RecommendationDto | undefined;
  roadmapId: string | undefined;
  isModalOpen: boolean;
  handleModalClose: () => void;
  onDownloadSuccess?: (fileName: string) => void;
  onDownloadError?: (fileName: string) => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const mb = k * k;
  
  if (bytes >= mb) {
    return (bytes / mb).toFixed(1) + ' MB';
  } else {
    return (bytes / k).toFixed(1) + ' KB';
  }
};

export const DownloadAttachmentsModal: React.FC<DownloadAttachmentsModalProps> = ({
  recommendation,
  roadmapId,
  isModalOpen,
  handleModalClose,
  onDownloadSuccess,
  onDownloadError
}) => {
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  
  const downloadTokenMutation = useGetAttachmentDownloadToken();
  const attachments = recommendation?.attachments || [];  

 const handleDownloadFile = async (attachment: AttachmentDto) => {
    if (!recommendation?.id || !roadmapId) return;

    // Prevent multiple downloads of the same file
    if (downloadingFiles.has(attachment.id)) return;

    setDownloadingFiles(prev => new Set(prev).add(attachment.id));

    try {
      // Get download token from backend
      const downloadUrl = await downloadTokenMutation.mutateAsync({
        roadmapId: roadmapId,
        recommendationId: recommendation.id,
        attachmentId: attachment.id
      });
      

      // Use fetch to download the blob and then create download link
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);

      // Call success callback
      onDownloadSuccess?.(attachment.fileName);

    } catch (error) {      
      onDownloadError?.(attachment.fileName);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const AttachmentItem: React.FC<{ attachment: AttachmentDto }> = ({ attachment }) => {
  const isDownloading = downloadingFiles.has(attachment.id);
  
    return (
      <div 
        data-testid={`attachment-item-${attachment.id}`}
        className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
          isDownloading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => !isDownloading && handleDownloadFile(attachment)}
      >
        {/* Download Icon */}
        <div 
          data-testid={`download-action-container-${attachment.id}`}
          className="flex-shrink-0 mr-3"
        >
          {isDownloading ? (
            <div 
              data-testid={`loading-spinner-${attachment.id}`}
              className="w-4 h-4"
            >
              <svg 
                data-testid={`spinner-icon-${attachment.id}`}
                className="animate-spin w-4 h-4 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle 
                  data-testid={`spinner-circle-${attachment.id}`}
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  data-testid={`spinner-path-${attachment.id}`}
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : (
            <div data-testid={`download-icon-${attachment.id}`}>
              <DownloadIcon classNames='text-gray-600' size='4' />
            </div>
          )}
        </div>
          
        {/* File Info */}
        <div 
          data-testid={`file-details-${attachment.id}`}
          className="flex-1 min-w-0"
        >
          <div 
            data-testid={`file-name-${attachment.id}`}
            className="text-sm text-black-900"
          >
            <span className="underline decoration-gray-200 decoration-1 underline-offset-4 hover:decoration-gray-600 pb-1">
              {attachment.fileName} ({formatFileSize(attachment.fileSize)})
            </span>            
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      data-testid='download-attachments-modal'
      headerTxt={
        <div 
          data-testid="modal-header-title"
          className="text-xlg text-gray-900"
        >
          Download attachments
        </div>
      }
      modalClassName={'mt-0'}
      isTruncatedHeader={false}
      isOpen={isModalOpen}
      size={isTablet || isMobile ? 'small' : 'medium'}
      position={isMobile ? 'bottom' : 'default'}
      showCloseBtn={true}
      onClose={handleModalClose}
      contentClassName="flex !flex-col !items-start !justify-start bg-white"
    >
      <div 
        data-testid="modal-content-container"
        className="w-full"
      >

        {/* Attachments List */}
        <div 
          data-testid="attachments-list-section"
          className={`w-full ${attachments.length > 5 ? 'max-h-60 overflow-y-auto' : ''}`}
        >
          {attachments.map((attachment, index) => (
              <div
                key={attachment.id}
                data-testid={`attachment-wrapper-${index}`}
              >
                <AttachmentItem 
                  attachment={attachment}
                />
              </div>
            ))}
        </div>

        
      </div>
    </Modal>
  );
};

export default DownloadAttachmentsModal;